import { useCallback, useRef, useState } from 'react';
import { ConfirmUploadLinkShareDialog } from '../components/ConfirmUploadLinkShareDialog';
import type { ConfirmDialogResolver } from './useConfirmCreateShareDialog';

/**
 * Provides the upload-link share confirmation dialog and a promise-based
 * trigger. Mirrors {@link useConfirmCreateShareDialog} (restricted items).
 *
 * @returns
 *   - confirmUploadLinkShareDialog: the dialog element to render
 *   - getConfirmUploadLinkShareResult: shows the dialog and resolves to the
 *     user's choice (true confirm, false cancel)
 */
export const useConfirmUploadLinkShareDialog = () => {
  const resolverRef = useRef<ConfirmDialogResolver | null>(null);
  const [isShowing, setIsShowing] = useState(false);

  const onResult = useCallback((result: boolean) => {
    setIsShowing(false);
    resolverRef.current?.(result);
    resolverRef.current = null;
  }, []);

  async function getConfirmUploadLinkShareResult() {
    return new Promise<boolean>((resolve) => {
      if (resolverRef.current) {
        throw new Error('Resolver already set');
      }
      resolverRef.current = resolve;
      setIsShowing(true);
    });
  }

  const confirmUploadLinkShareDialog = (
    <ConfirmUploadLinkShareDialog isShowing={isShowing} onConfirm={onResult} />
  );

  return {
    confirmUploadLinkShareDialog,
    getConfirmUploadLinkShareResult,
  };
};
